import classNames from 'classnames';
import { FormikProvider, useFormik } from 'formik';
import { pick } from 'lodash';
import { useMemo, useState } from 'react';
import * as yup from 'yup';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import {
  AUS_POSTCODE_REGEX,
  USA_POSTCODE_REGEX,
  phoneRegExp,
} from '@/modules/common/utils/regex.ts';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { CountryNameCodeEnum } from '@/modules/entity/static/countries.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import {
  useShareholderCommunicationSettingQuery,
  useShareholderCommunicationSettingUpdateMutation,
} from '@/modules/investor-dashboard/queries/shareholders.tsx';
import {
  Button,
  CustomSwitch,
  Select,
  SelectButtons,
  SelectWithCreatable,
  Spinner,
  TextInputField,
  getSelectOptionByEnum,
} from 'ui';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { EntityPreferredContactWay, ShareholderExtend, UpdateShareholderRequest } from 'api';

const validationSchema = yup.object<UpdateShareholderRequest>({
  address: yup.string().trim().max(80).nullable(),
  phone_number: yup
    .string()
    .trim()
    .matches(
      phoneRegExp,
      "Phone number doesn't match our format, here are a few examples: +61 412 345 678, 11234567890",
    )
    .required('This field is required'),
  email: yup.string().trim().email('Must be a valid email').required('This field is required'),
  additional_emails: yup
    .array()
    .of(yup.string().trim().required('This field is required').email('Must be a valid email'))
    .nullable(),
  notifications_enabled: yup.boolean(),
  preferred_contact_way: yup
    .mixed<EntityPreferredContactWay>()
    .oneOf(Object.values(EntityPreferredContactWay)),
  suburb: yup.string().trim().required('This field is required'),
  state: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS' || country === 'USA') {
        return schema.required('This field is required');
      }

      return schema.nullable();
    }),
  postcode: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS' || country === 'NZL') {
        return schema.matches(AUS_POSTCODE_REGEX, 'Invalid postcode format, e.g. 2000');
      }

      if (country === 'USA') {
        return schema.matches(USA_POSTCODE_REGEX, 'Invalid postcode format, e.g. 20001');
      }

      return schema;
    })
    .required('This field is required'),
  country: yup.string().trim().required('This field is required'),
});

interface EditEntityCommunicationSettingsTabProps {
  entityId: number;
}

export const EditEntityCommunicationSettingsTab = ({
  entityId,
}: EditEntityCommunicationSettingsTabProps) => {
  const { data: dataCommunicationSetting, isLoading } =
    useShareholderCommunicationSettingQuery(entityId);

  const { isLoading: isLoadingUpdate, mutateAsync: mutateAsyncUpdate } =
    useShareholderCommunicationSettingUpdateMutation();
  const [currentRegister, setCurrentRegister] = useState(0);

  const data = useMemo(
    () => dataCommunicationSetting?.data?.[currentRegister],
    [currentRegister, dataCommunicationSetting?.data],
  );

  const getInitialValues = (data?: ShareholderExtend): UpdateShareholderRequest => {
    const current = pick(data, [
      'id',
      'email',
      'phone_number',
      'address',
      'additional_emails',
      'notifications_enabled',
      'preferred_contact_way',
      'additional_address',
      'suburb',
      'state',
      'postcode',
      'country',
    ]) as UpdateShareholderRequest;
    return {
      ...current,
      preferred_contact_way: current?.preferred_contact_way ?? EntityPreferredContactWay.Email,
    } as UpdateShareholderRequest;
  };

  const formik = useFormik<UpdateShareholderRequest>({
    validationSchema,
    initialValues: getInitialValues(data),
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      if (data) {
        const additionalEmails = values.additional_emails?.filter(item => !!item);
        await mutateAsyncUpdate({
          ...values,
          additional_emails: (additionalEmails?.length || 0) > 0 ? additionalEmails : [],
          id: data.id,
        });
      }
    },
  });

  const handleChangeSelect = (
    field: string,
    value: string | boolean | EntityPreferredContactWay,
  ) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  const handleClickDeleteButton = () => {
    if (!formik.values.additional_emails) return;
    formik.setFieldValue('additional_emails', [
      ...formik.values.additional_emails.slice(0, formik.values.additional_emails.length - 1),
    ]);
  };

  const TrashButton = (
    <button
      key="trash"
      type="button"
      className="absolute inset-y-0 right-0 flex items-center"
      onClick={handleClickDeleteButton}
    >
      <img src={TrashIcon} alt="delete" />
    </button>
  );

  return (
    <FormikProvider value={formik}>
      <div className="py-4">
        {isLoading ? (
          <div className="flex flex-col justify-center items-center h-[200px] gap-8">
            <Spinner size="lg" />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="flex gap-4 flex-wrap">
              {dataCommunicationSetting?.data?.map((item, i) => (
                <div key={item.id}>
                  <Button
                    variant="secondary"
                    active={i === currentRegister}
                    onClick={() => setCurrentRegister(i)}
                  >
                    {item.register_obj.name}
                  </Button>
                </div>
              ))}
            </div>
            <div className="flex flex-col gap-1">
              <span
                className={classNames(
                  'flex flex-row justify-between text-[14px] leading-[20px]',
                  'font-[Inter] text-gray-900 font-medium',
                )}
              >
                Preferred way to communicate with{' '}
                {dataCommunicationSetting?.data?.[currentRegister]?.register_obj?.name ?? ''}
              </span>
              <span
                className={classNames(
                  'flex flex-row justify-between text-[12px] leading-[20px]',
                  'font-[Inter] text-gray-600 font-medium',
                )}
              >
                The issuer will use this channel to send you all communications
              </span>
              <SelectButtons
                options={getSelectOptionByEnum<EntityPreferredContactWay>(
                  EntityPreferredContactWay,
                )}
                selected={getSelectOptionByEnum<EntityPreferredContactWay>(
                  EntityPreferredContactWay,
                ).find(({ value }) => value === formik.values.preferred_contact_way)}
                onChange={value => {
                  handleChangeSelect('preferred_contact_way', value);
                }}
              />
            </div>
            <CustomSwitch
              checked={formik.values.notifications_enabled}
              onChange={() => {
                formik.setFieldValue('notifications_enabled', !formik.values.notifications_enabled);
              }}
              className="text-[14px]"
              label="Automatic email notifications"
              subLabel="Compli One will automatically notify you about transaction status updated, payment requests, transfers and dividends"
            />
            <TextInputField name="email" label="Email address" placeholder="Email" />
            <TextInputField name="phone_number" label="Phone number" placeholder="Phone number" />

            <span className="text-[16px] leading-6 text-gray-900 font-[Inter] font-[500]">
              Postal address
            </span>

            <TextInputField
              name="address"
              label="Address Line 1 (Street Address)"
              placeholder="Address"
            />

            <TextInputField
              name="additional_address"
              label="Address Line 2 (Optional)"
              placeholder="Address"
            />

            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField label="postcode" name="postcode" />
              <Select
                isClearable
                label="Country"
                options={getSelectOptionByEnum<CountryNameCodeEnum>(CountryNameCodeEnum, true)}
                value={formik.values.country as unknown as CountryNameCodeEnum}
                onChange={value => handleChangeSelect('country', value || '')}
                error={formik.errors.country}
                positionMenu="up"
              />
            </div>

            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <TextInputField name="suburb" label="Suburb/City" />
              <SelectWithCreatable
                isClearable={true}
                withCreatable={true}
                label="State/Territory"
                key={`state_country_${formik.values.country}`}
                options={
                  (formik.values.country as unknown as CountryNameCodeEnum) === 'AUS'
                    ? auStateOptions
                    : (formik.values.country as unknown as CountryNameCodeEnum) === 'USA'
                    ? usaStateOptions
                    : []
                }
                value={
                  findDefaultValue(
                    (formik.values.country as unknown as CountryNameCodeEnum) === 'AUS'
                      ? auStateOptions
                      : (formik.values.country as unknown as CountryNameCodeEnum) === 'USA'
                      ? usaStateOptions
                      : [],
                    formik.values.state,
                  ) || { value: formik.values.state, label: formik.values.state }
                }
                onChange={option => handleChangeSelect('state', option?.value || '')}
                error={formik.errors.state}
                positionMenu="up"
              />
            </div>

            <div className="w-full flex sm:flex-row flex-col items-start justify-between gap-2">
              <span className="font-[Inter] flex flex-col">
                <span className="text-gray-900 text-[16px] leading-6 font-[500]">
                  Additional emails
                </span>
                <span className="text-gray-600 text-[12px] leading-5">
                  For Advisors, accountants or anyone else who needs updates
                </span>
              </span>

              <div className="w-[130px]">
                <Button
                  variant="plain"
                  className="px-0"
                  onClick={() => {
                    formik.setFieldValue('additional_emails', [
                      ...(formik.values.additional_emails || []),
                      '',
                    ]);
                    formik.setErrors({});
                  }}
                >
                  <img src={PlusBlueIcon} alt="plus" className="pr-1" /> Add new email
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <>
                {(formik.values.additional_emails || []).map((_, i, { length }) => (
                  <TextInputField
                    key={`additional_emails.${i}`}
                    name={`additional_emails.${i}`}
                    placeholder="Enter email"
                    buttons={length - 1 === i ? [TrashButton] : undefined}
                  />
                ))}
              </>
            </div>
          </div>
        )}
      </div>
      <Button
        loading={isLoadingUpdate}
        disabled={!formik.dirty || isLoadingUpdate}
        onClick={formik.handleSubmit}
        type="submit"
      >
        Save changes
      </Button>
    </FormikProvider>
  );
};
